/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file ChatHeaderButton.js
 * @description
 * Messenger hub entry point for headers (management TopBar, POS CustomHeader): a
 * chat-bubble glyph with the COMBINED unread badge (team chat + live support;
 * red when a mention is pending, brand orange otherwise) that opens the
 * `ChatHomeScreen` hub. Renders nothing when the
 * build disables chat or the store/role is not entitled (403), so no header
 * ever advertises a module the user cannot open.
 */

import React, {useCallback} from 'react';
import {Text, TouchableOpacity, View} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';
import SafeIcon from '../management/SafeIcon';
import {useChatHubBadge} from '../../helpers/teamChat/useChatHubBadge';

/** Brand orange (chat accents). */
export const CHAT_BRAND = '#F97316';

/** Anything above this renders as "99+". */
const BADGE_DISPLAY_CAP = 99;

/**
 * Format a badge count.
 * @param {number} count
 * @returns {string}
 */
export const formatBadgeCount = (count) => (count > BADGE_DISPLAY_CAP ? `${BADGE_DISPLAY_CAP}+` : String(count));

/**
 * Small count pill.
 * @param {{count: number, mention?: boolean, style?: Object}} props
 * @returns {?JSX.Element}
 */
export const ChatCountBadge = ({count, mention = false, style}) => {
    const value = Number(count) || 0;
    if (value <= 0) {
        return null;
    }

    return (
        <View style={[styles.badge, {backgroundColor: mention ? '#EF4444' : CHAT_BRAND}, style]}>
            <Text style={styles.badgeText} numberOfLines={1}>{formatBadgeCount(value)}</Text>
        </View>
    );
};

/**
 * Header chat button with unread badge.
 * @param {object} props
 * @param {number} [props.size=22] Glyph size.
 * @param {string} [props.color] Glyph colour.
 * @param {Object} [props.style] Touchable style.
 * @param {string} [props.iconName='forum-outline'] Glyph.
 * @returns {?JSX.Element}
 */
const ChatHeaderButton = ({size = 22, color, style, iconName = 'forum-outline'}) => {
    const navigation = useNavigation();
    const {t} = useTranslation();
    const {visible, total, mention} = useChatHubBadge();

    const onPress = useCallback(() => {
        navigation.navigate('ChatHomeScreen');
    }, [navigation]);

    if (!visible) {
        return null;
    }

    return (
        <TouchableOpacity
            onPress={onPress}
            activeOpacity={0.7}
            hitSlop={12}
            accessibilityRole="button"
            accessibilityLabel={t('team_chat_hub_title')}
            style={style}
        >
            <View style={styles.wrap}>
                <SafeIcon name={iconName} fallback="message-outline" size={size} color={color} />
                <ChatCountBadge count={total} mention={mention} style={styles.badgePosition} />
            </View>
        </TouchableOpacity>
    );
};

const styles = {
    wrap: {position: 'relative'},
    badgePosition: {position: 'absolute', top: -6, right: -8},
    badge: {
        minWidth: 18,
        height: 18,
        paddingHorizontal: 4,
        borderRadius: 9,
        alignItems: 'center',
        justifyContent: 'center',
    },
    badgeText: {
        color: '#FFFFFF',
        fontSize: 10.5,
        fontWeight: '800',
        lineHeight: 14,
    },
};

export default React.memo(ChatHeaderButton);
